/**
 * Figure S6's "Possible duplicated effort" card — the top of the Portfolio
 * Overview's right rail. Groups of projects working the same ground, so the
 * portfolio funds the work once rather than several times.
 *
 * Every line is a real number from the overlap run: the group's member names,
 * its strongest pair score, and whether the run has yet marked it new. The
 * card never invents a group, and it says plainly what the scan was computed
 * over.
 */
import { Layers } from 'lucide-react'
import type { OverlapGroup } from '../../types/whitespace'

export interface DuplicatedEffortCardProps {
  groups: OverlapGroup[]
  /** project_id -> name, from the portfolio payload. */
  projectNames: Map<string, string>
  onOpenOverlap: () => void
  onOpenGroup?: (groupId: string) => void
  /** True while a compute run is still in flight. */
  computing?: boolean
}

/** A group the run has not been through with a human yet. */
export function isNewGroup(group: OverlapGroup): boolean {
  return group.lifecycle_status === 'new'
}

export function newGroupCount(groups: OverlapGroup[]): number {
  return groups.filter(isNewGroup).length
}

/** "Kestrel Robotics · Vantage Biotech" — the group's members, named. */
export function groupTitle(group: OverlapGroup, names: Map<string, string>): string {
  const labelled = group.member_project_ids.map((id) => names.get(id)).filter((n): n is string => !!n)
  if (labelled.length === 0) return `${group.member_project_ids.length} projects`
  if (labelled.length <= 3) return labelled.join(' · ')
  return `${labelled.slice(0, 2).join(' · ')} and ${labelled.length - 2} more`
}

/**
 * What the run actually establishes about a group.
 *
 * `worth_a_look` does NOT mean "scored low". It means the group was formed by
 * the minimum-evidence floor — too few documents to be sure — and on such a
 * group the pair coefficient says nothing a reader should weigh, because it
 * was computed over that thin evidence. Printing them together produced
 * "thin evidence, not yet a verdict · strongest pair 100" on the live screen,
 * which contradicts itself and invites exactly the confidence the first half
 * withholds. So the score is quoted for a SCORED group and never for a
 * worth-a-look one.
 */
export function evidenceLine(group: OverlapGroup): string {
  if (group.worth_a_look) return 'Worth a look — thin evidence, not yet a verdict'
  return typeof group.score === 'number'
    ? `Substantially the same work · strongest pair ${group.score}`
    : 'Substantially the same work'
}

const MAX_ROWS = 3

export default function DuplicatedEffortCard({
  groups,
  projectNames,
  onOpenOverlap,
  onOpenGroup,
  computing = false,
}: DuplicatedEffortCardProps) {
  const newest = [...groups].sort((a, b) => Number(isNewGroup(b)) - Number(isNewGroup(a)))
  const shown = newest.slice(0, MAX_ROWS)
  const newCount = newGroupCount(groups)

  return (
    <div data-part="DuplicatedEffortCard" className="light-card p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-[15px] font-bold text-iflow-900">
          <Layers size={16} className="text-iflow-500" aria-hidden /> Possible duplicated effort
        </h3>
        {newCount > 0 && (
          <span className="shrink-0 rounded-full bg-iflow-100 px-2 py-0.5 text-[11.5px] font-semibold text-iflow-700">
            {newCount} new
          </span>
        )}
      </div>
      <p className="mt-1 text-[12px] text-gray-500">
        Groups of projects working the same ground. Fund once, not twice.
      </p>

      {computing ? (
        <p className="mt-3 text-[12.5px] text-gray-600">
          Comparing your portfolio for projects working the same ground — this can take a few minutes.
        </p>
      ) : shown.length === 0 ? (
        <p className="mt-3 text-[12.5px] text-gray-600">
          No two of your portfolio&apos;s projects currently score as working the same ground.
        </p>
      ) : (
        <ul className="mt-2">
          {shown.map((group) => (
            <li
              key={group.group_id}
              data-testid="duplicated-effort-row"
              className="flex items-start justify-between gap-2 border-t border-iflow-900/5 py-2 first:border-t-0"
            >
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => onOpenGroup?.(group.group_id)}
                  className="text-left text-[13px] font-semibold text-iflow-900 hover:underline"
                >
                  {groupTitle(group, projectNames)}
                </button>
                <div className="text-[11.5px] text-gray-500">{evidenceLine(group)}</div>
              </div>
              {isNewGroup(group) && (
                <span className="shrink-0 rounded-full bg-iflow-100 px-2 py-0.5 text-[11px] font-semibold text-iflow-700">
                  New
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-2 text-[11px] italic text-gray-500">
        computed over everything you&apos;re entitled to see
      </p>
      <div className="mt-1 text-right">
        <button
          type="button"
          onClick={onOpenOverlap}
          className="text-[12px] font-semibold text-iflow-700 hover:text-iflow-800 hover:underline"
        >
          Open Overlap →
        </button>
      </div>
    </div>
  )
}
